import { submissionApiClient } from "@/api/submission-api";
import { profileApi } from "@/api/profile-api";
import { workspaceApi } from "@/api/workspace-api";
import ApplicationStep from "@/components/portal/steps/application-step";
import BasicInfoStep from "@/components/portal/steps/basic-info-step";
import CompanyInfoStep from "@/components/portal/steps/company-info-step";
import PortcoUpdateStep from "@/components/portal/steps/portco-update-step";
import UploadDocumentsStep from "@/components/portal/steps/upload-documents-step";
import SubmissionHeader from "@/components/portal/submission-header";
import { useAuthStore } from "@/store/auth-store";
import { IWorkspace } from "@/types/data-models";
import { Building2 } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { setPostHogGroup } from "@/lib/posthog";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router";

export default function SubmissionPage() {
  const [searchParams] = useSearchParams();
  const { slug } = useParams<{ slug: string }>();
  const { isInitialized, isLoading, checkAuth } = useAuthStore();
  const { user: clerkUser, isLoaded: isClerkLoaded } = useUser();
  const navigate = useNavigate();
  const opportunityId = searchParams.get("id");
  const hasCalledMeApi = useRef(false);

  // Convert Clerk user to the format expected by the rest of the app
  const user = useMemo(() => {
    if (!clerkUser) return null;
    return {
      id: clerkUser.id,
      email: clerkUser.primaryEmailAddress?.emailAddress || "",
      firstName: clerkUser.firstName || "",
      lastName: clerkUser.lastName || "",
    };
  }, [
    clerkUser?.id,
    clerkUser?.primaryEmailAddress?.emailAddress,
    clerkUser?.firstName,
    clerkUser?.lastName,
  ]);

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [opportunityData, setOpportunityData] = useState<IWorkspace | null>(
    null
  );

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    companyName: "",
    foundedMonth: new Date().getMonth() + 1,
    foundedYear: new Date().getFullYear(),
  });

  const [companyInfo, setCompanyInfo] = useState<any>(null);
  const [, setApplicationStatus] = useState<{
    hasApplied: boolean;
    applicationStatus: string | null;
    applicationId: string | null;
  } | null>(null);
  const [existingApplicationData, setExistingApplicationData] =
    useState<any>(null);
  const [isPortfolioCompany, setIsPortfolioCompany] = useState<boolean | null>(
    null
  );
  const [hasInitializedUserData, setHasInitializedUserData] = useState(false);
  const [isInitializingUserData, setIsInitializingUserData] = useState(false);

  useEffect(() => {
    if (!isInitialized && !isLoading) {
      checkAuth().catch((err) => {
        console.error("Error initializing auth state:", err);
      });
    }
  }, [checkAuth, isInitialized, isLoading]);

  // Call /me API on page load to get user and company info early
  useEffect(() => {
    const fetchUserProfile = async () => {
      // Prevent multiple calls
      if (hasCalledMeApi.current) {
        console.log("⏭️ /me API already called, skipping...");
        return;
      }

      console.log("🔍 fetchUserProfile called", {
        isClerkLoaded,
        hasUser: !!user,
        userId: user?.id,
        hasCompanyInfo: !!companyInfo,
      });

      // Wait for Clerk to be loaded
      if (!isClerkLoaded) {
        console.log("⏳ Waiting for Clerk to load...");
        return;
      }

      // Only fetch if user is logged in and we don't already have company info
      if (user && user.id && !companyInfo) {
        hasCalledMeApi.current = true;
        console.log("✅ Calling /me API...");
        try {
          const profileResponse = await profileApi.getMe();
          console.log("📥 Profile response on page load:", profileResponse);

          if (profileResponse.success && profileResponse.company) {
            // Convert the company profile to the format expected by the rest of the app
            const companyData = {
              _id: profileResponse.company.id,
              company_name: profileResponse.company.companyName,
              short_bio: profileResponse.company.shortBio,
              detailed_description: profileResponse.company.detailedDescription,
              company_website: profileResponse.company.website,
              industry: profileResponse.company.industry,
              company_type: profileResponse.company.companyType,
              stage: profileResponse.company.stage,
              city: profileResponse.company.city,
              province: profileResponse.company.province,
              country: profileResponse.company.country,
              address: profileResponse.company.address,
              contact_phone: profileResponse.company.contactPhone,
              business_model: profileResponse.company.businessModel,
              // GICS hierarchy (2-level)
              gics_industry: profileResponse.company.gics_industry,
              gics_sub_industry: profileResponse.company.gics_sub_industry,
              // Revenue model
              revenue_model: profileResponse.company.revenue_model,
              facebook: profileResponse.company.facebook,
              twitter: profileResponse.company.twitter,
              linkedIn: profileResponse.company.linkedIn,
              instagram: profileResponse.company.instagram,
            };

            console.log(
              "✅ Company data from profile (page load):",
              companyData
            );
            setCompanyInfo(companyData);
          } else {
            console.log("ℹ️ User does not have a company (page load)");
          }
        } catch (err) {
          console.error("❌ Error fetching user profile on page load:", err);
          hasCalledMeApi.current = false; // Reset on error so we can retry
          // Don't fail the whole page if this fails
        }
      } else {
        if (companyInfo) {
          console.log("ℹ️ Already have company info, skipping /me API call");
        } else {
          console.log("ℹ️ No user found, skipping /me API call");
        }
      }
    };

    fetchUserProfile();
  }, [isClerkLoaded, user?.id, companyInfo]);

  useEffect(() => {
    const initializeSubmission = async () => {
      if (!opportunityId && !slug) {
        setError("No opportunity ID or slug provided");
        setLoading(false);
        return;
      }

      // Wait for auth store to be initialized
      if (!isInitialized || isLoading) {
        return;
      }

      // Reset per-opportunity state so stale values don't persist across slug/id changes
      setLoading(true);
      setExistingApplicationData(null);
      setIsPortfolioCompany(false);
      setHasInitializedUserData(false);

      try {
        // Fetch opportunity data by slug or ID
        const response = slug
          ? await workspaceApi.getPortalWorkspaceBySlug(slug)
          : await workspaceApi.getPortalWorkspace(opportunityId!);
        console.log("Fetched opportunity data:", response.result);
        setOpportunityData(response.result);
        const resolvedOpportunityId = response.result._id;

        // Link subsequent portal events to the workspace group so PostHog
        // records the person↔workspace relationship for founders visiting
        // the portal (they never hit the main-app workspace store).
        if (resolvedOpportunityId) {
          setPostHogGroup("workspace", resolvedOpportunityId, {
            name: response.result.opportunity_name,
          });
        }

        // If accessed via legacy ID route and workspace has a slug, redirect to slug URL
        if (opportunityId && !slug && response.result.portal_slug) {
          const currentParams = new URLSearchParams(window.location.search);
          currentParams.delete('id');
          const queryString = currentParams.toString();
          const redirectUrl = `/p/${response.result.portal_slug}/submission${queryString ? `?${queryString}` : ''}`;
          navigate(redirectUrl, { replace: true });
          return;
        }

        console.log("Checking user", user);

        // NOTE: do NOT gate this on `hasInitializedUserData`. The closure
        // captures a stale value, and the state is reset to `false` a few
        // lines above on every effect run, which previously combined with
        // `hasInitializedUserData` being in the deps array to create an
        // infinite re-fetch / re-render loop on this page (SAAS-601).
        // Re-runs are now controlled solely by the deps array — slug,
        // opportunityId, auth state, and user identity.
        if (user) {
          let isPortco = false;
          let hasCompanyData = false;
          let applicationStatusResult = null;

          console.log("User is found", user);
          setIsInitializingUserData(true);

          // Check application status
          try {
            const statusResponse =
              await submissionApiClient.application.checkApplicationStatus(
                resolvedOpportunityId,
                user.id
              );

            applicationStatusResult = statusResponse;

            setApplicationStatus({
              hasApplied: statusResponse.hasApplied,
              applicationStatus: statusResponse.applicationStatus,
              applicationId: statusResponse.applicationId,
            });

            // Check if this is a portfolio company
            isPortco =
              statusResponse.applicationStatus === "Portfolio Companies";
            setIsPortfolioCompany(isPortco);

            // If application exists, we'll fetch the data after we have company info
            if (statusResponse.hasApplied) {
              console.log(
                "✅ Application exists, will fetch data after company info is loaded"
              );
            } else {
            }
          } catch (err) {
            console.error("Error checking application status:", err);
            // Don't fail the whole page if this check fails
            setApplicationStatus({
              hasApplied: false,
              applicationStatus: null,
              applicationId: null,
            });
            setIsPortfolioCompany(false);
          }

          // Check if user has company data using the /me endpoint
          try {
            const profileResponse = await profileApi.getMe();
            console.log("Profile response:", profileResponse);

            if (profileResponse.success && profileResponse.company) {
              // Convert the company profile to the format expected by the rest of the app
              const companyData = {
                _id: profileResponse.company.id,
                company_name: profileResponse.company.companyName,
                short_bio: profileResponse.company.shortBio,
                detailed_description:
                  profileResponse.company.detailedDescription,
                company_website: profileResponse.company.website,
                industry: profileResponse.company.industry,
                company_type: profileResponse.company.companyType,
                stage: profileResponse.company.stage,
                city: profileResponse.company.city,
                province: profileResponse.company.province,
                country: profileResponse.company.country,
                address: profileResponse.company.address,
                contact_phone: profileResponse.company.contactPhone,
                business_model: profileResponse.company.businessModel,
                // GICS hierarchy (2-level)
                gics_industry: profileResponse.company.gics_industry,
                gics_sub_industry: profileResponse.company.gics_sub_industry,
                // Revenue model
                revenue_model: profileResponse.company.revenue_model,
                facebook: profileResponse.company.facebook,
                twitter: profileResponse.company.twitter,
                linkedIn: profileResponse.company.linkedIn,
                instagram: profileResponse.company.instagram,
              };

              console.log("Company data from profile:", companyData);
              setCompanyInfo(companyData);
              hasCompanyData = true;

              // If application exists, fetch the application data now that we have company info
              // We need to check the applicationStatusResult from the outer scope, not the state
              if (applicationStatusResult?.hasApplied) {
                console.log(
                  "🔄 Fetching application data for company:",
                  companyData._id
                );
                try {
                  const applicationDataResponse =
                    await submissionApiClient.application.getApplicationData(
                      companyData._id,
                      resolvedOpportunityId
                    );
                  console.log(
                    "📥 Raw application data response:",
                    applicationDataResponse
                  );
                  setExistingApplicationData(applicationDataResponse.result);
                  console.log(
                    "✅ Successfully set existing application data:",
                    applicationDataResponse.result
                  );
                } catch (err) {
                  console.error(
                    "❌ Error fetching existing application data:",
                    err
                  );
                  // Don't fail the whole flow if this fails
                }
              }
            } else {
              console.log("User does not have a company yet");
            }
          } catch (err) {
            console.error("Error fetching user profile:", err);
            // User doesn't have company data, will start from step 1
          }

          // Set the appropriate step based on portfolio company status and company data
          console.log(
            "Step determination - hasCompanyData:",
            hasCompanyData,
            "isPortco:",
            isPortco
          );
          if (hasCompanyData) {
            if (isPortco) {
              setCurrentStep(4); // Portfolio companies go directly to step 4 (portco update)
            } else {
              setCurrentStep(2); // Regular companies go to step 2 (upload documents)
            }
          } else {
            setCurrentStep(1); // No company data, start from step 1
          }

          setHasInitializedUserData(true);
          setIsInitializingUserData(false);
        } else if (!user) {
          // No user, start from step 1
          setCurrentStep(1);
        }
      } catch (err) {
        console.error("Error initializing submission:", err);
        setError("Failed to load opportunity data");
      } finally {
        setLoading(false);
      }
    };

    initializeSubmission();
    // `hasInitializedUserData` is intentionally NOT in this deps array — it
    // is written inside this effect, so including it would cause an infinite
    // loop. See the comment in the `if (user)` block above.
  }, [opportunityId, slug, isInitialized, isLoading, user?.id]);

  // Redirect to step 1 if we're on step 3 but don't have company info after initialization
  useEffect(() => {
    if (
      currentStep === 2 &&
      !companyInfo &&
      !isInitializingUserData &&
      hasInitializedUserData &&
      !isPortfolioCompany
    ) {
      console.log("No company info found, redirecting to step 1");
      setCurrentStep(1);
    }
  }, [
    currentStep,
    companyInfo,
    isInitializingUserData,
    hasInitializedUserData,
    isPortfolioCompany,
  ]);

  const nextStep = useCallback(() => {
    // For portfolio companies, we have 2 steps total (1 -> 4)
    // For regular companies, we have 4 steps total (1 -> 2 -> 3 -> 4)
    if (isPortfolioCompany) {
      // Portfolio companies: step 1 -> step 4
      if (currentStep === 1) {
        setCurrentStep(4);
      }
    } else {
      // Regular companies: step 1 -> 2 -> 3 -> 4
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      }
    }
  }, [currentStep, isPortfolioCompany]);

  const prevStep = useCallback(() => {
    if (isPortfolioCompany) {
      // Portfolio companies: step 1 (auth) -> step 4
      // Can't go back from step 4 — step 1 is auth gate, steps 2-3 were skipped
      return;
    } else {
      // Regular companies: step 4 -> 3 -> 2 (step 1 is auth gate, never go back)
      if (currentStep > 2) {
        setCurrentStep(currentStep - 1);
      }
    }
  }, [currentStep, isPortfolioCompany]);

  // Track which steps have been completed for clickable step navigation
  useEffect(() => {
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      if (isPortfolioCompany) {
        // Portfolio flow: step 1 is auth gate (not navigable), steps 2-3 skipped
        // No steps are clickable for portfolio companies
      } else {
        // Regular flow: steps 2+ before current are completed
        // Step 1 is auth gate — once past it, never go back
        for (let s = 2; s < currentStep; s++) {
          next.add(s);
        }
      }
      return next;
    });
  }, [currentStep, isPortfolioCompany]);

  const goToStep = useCallback(
    (step: number) => {
      // Only allow navigating to completed steps that are before the current step
      if (step >= 1 && step < currentStep && completedSteps.has(step)) {
        setCurrentStep(step);
      }
    },
    [currentStep, completedSteps]
  );


  const handleCompanyInfoUpdate = useCallback(
    (updatedCompanyInfo: any) => {
      console.log("handleCompanyInfoUpdate called with:", updatedCompanyInfo);
      console.log("Current companyInfo before update:", companyInfo);

      // Map form field names (camelCase) back to API field names (snake_case)
      // This ensures data persists correctly when navigating back to step 2
      const mappedInfo = {
        company_website: updatedCompanyInfo.companyWebsite,
        linkedIn: updatedCompanyInfo.linkedInUrl,
        address: updatedCompanyInfo.address,
        country: updatedCompanyInfo.country,
        // GICS hierarchy (2-level)
        gics_industry: updatedCompanyInfo.gicsIndustry,
        gics_sub_industry: updatedCompanyInfo.gicsSubIndustry,
        // Business & Revenue (multi-select — arrays of short codes)
        business_model: updatedCompanyInfo.businessModel,
        revenue_model: updatedCompanyInfo.revenueModel,
        stage: updatedCompanyInfo.companyStage,
        contact_phone: updatedCompanyInfo.contactPhone,
      };

      // Merge the updated info with existing company data to preserve _id
      setCompanyInfo((prevCompanyInfo: any) => {
        const mergedInfo = {
          ...prevCompanyInfo,
          ...mappedInfo,
        };
        console.log("Merged company info:", mergedInfo);
        return mergedInfo;
      });
      nextStep();
    },
    [nextStep, companyInfo]
  );

  const getCompletionUrl = () =>
    slug
      ? `/p/${slug}`
      : opportunityData?.portal_slug
        ? `/p/${opportunityData.portal_slug}`
        : `/portal?id=${opportunityId}`;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInfoStep
            formData={formData}
            setFormData={setFormData}
            nextStep={nextStep}
            setCompanyInfo={setCompanyInfo}
          />
        );
      case 2:
        // Only show for non-portfolio companies
        if (isPortfolioCompany) {
          return null;
        }
        // If we don't have company info and we're done initializing, redirect to step 1
        if (!companyInfo && !isInitializingUserData && hasInitializedUserData) {
          // Use useEffect to redirect (handled below)
          return (
            <div className="max-w-2xl mx-auto p-4 md:p-6">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <h2 className="text-2xl font-bold mb-4">No Company Found</h2>
                <p className="text-gray-600">
                  Please create a company to continue with your application.
                </p>
              </div>
            </div>
          );
        }
        // Show loading only if we're still initializing
        if (!companyInfo) {
          return (
            <div className="max-w-2xl mx-auto p-4 md:p-6">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <h2 className="text-2xl font-bold mb-4">Loading...</h2>
                <p className="text-gray-600">Loading company information...</p>
              </div>
            </div>
          );
        }
        return (
          <CompanyInfoStep
            companyData={companyInfo}
            onNext={handleCompanyInfoUpdate}
          />
        );
      case 3:
        // Only show for non-portfolio companies
        if (isPortfolioCompany) {
          return null;
        }
        return (
          <UploadDocumentsStep
            companyData={companyInfo}
            opportunityId={opportunityData!._id}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 4:
        // Check if user is a portfolio company
        if (isPortfolioCompany && opportunityData && companyInfo) {
          return (
            <PortcoUpdateStep
              opportunityData={opportunityData}
              companyData={companyInfo}
              onComplete={() => navigate(getCompletionUrl())}
            />
          );
        }

        // Regular application step for non-portfolio companies
        console.log(
          "🎯 Rendering ApplicationStep with existingApplicationData:",
          existingApplicationData
        );
        return opportunityData && companyInfo ? (
          <ApplicationStep
            opportunityData={opportunityData}
            companyData={companyInfo}
            existingApplicationData={existingApplicationData}
            onBack={prevStep}
            onComplete={() => navigate(getCompletionUrl())}
          />
        ) : (
          <div className="max-w-2xl mx-auto p-4 md:p-6">
            <div className="bg-white rounded-lg p-6 shadow-lg">
              <h2 className="text-2xl font-bold mb-4">Loading...</h2>
              <p className="text-gray-600">Loading application form...</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Show loading while auth is initializing or while loading submission data
  if (!isInitialized || isLoading || loading || isInitializingUserData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {!isInitialized || isLoading
              ? "Initializing..."
              : isInitializingUserData
                ? "Loading your data..."
                : "Loading submission form..."}
          </p>
        </div>
      </div>
    );
  }

  if (error || !opportunityData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-amber-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">
              Unable to Load Submission Form
            </h2>
            <p className="text-red-600 mb-4">
              {error || "Opportunity not found"}
            </p>
            <button
              onClick={() => navigate(-1)}
              type="button"
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh overflow-y-auto bg-gradient-to-br from-yellow-50 via-white to-amber-50">
      <SubmissionHeader
        activeStep={currentStep}
        completedSteps={completedSteps}
        onBack={currentStep > 2 ? prevStep : undefined}
        onGoToStep={goToStep}
      />

      {/* Step title */}
      <div className="text-center mt-4 md:mt-8 mb-3 md:mb-6 px-4">
        <h2 className="text-lg md:text-xl font-semibold text-gray-900">
          Step {currentStep} of 4:{" "}
          {
            ["Basic Info", "Company Info", "Upload Documents", "Application"][
            currentStep - 1
            ]
          }
        </h2>
        <p className="text-xs md:text-sm text-gray-600 mt-1">
          {currentStep === 1 &&
            "Let's get started with your basic information"}
          {currentStep === 2 && "Tell us more about your company"}
          {currentStep === 3 && "Upload your essential company documents"}
          {currentStep === 4 && "Complete your application"}
        </p>
      </div>

      {/* Opportunity Info Card */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 pb-3">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
          <div className="flex items-center gap-3">
            {opportunityData.affiliated_company?.files?.find(
              (file) => file.file_type === "company avatar"
            )?.file_url ? (
              <img
                src={
                  opportunityData.affiliated_company?.files?.find(
                    (file) => file.file_type === "company avatar"
                  )?.file_url!
                }
                alt="Program Logo"
                className="w-8 h-8 rounded object-contain bg-gray-100 flex-shrink-0"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  e.currentTarget.nextElementSibling?.classList.remove(
                    "hidden"
                  );
                }}
              />
            ) : null}
            <div
              className={`w-8 h-8 rounded flex items-center justify-center bg-gray-100 flex-shrink-0 ${opportunityData.affiliated_company?.files?.find(
                (file) => file.file_type === "company avatar"
              )?.file_url
                ? "hidden"
                : ""
                }`}
            >
              <Building2 className="w-4 h-4 text-gray-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm text-gray-900 truncate">
                {opportunityData.opportunity_name}
              </h3>
              <p className="text-xs text-gray-500">
                {opportunityData.opportunity_type}
              </p>
              <div className="flex items-center gap-3 text-xs text-gray-600 mt-1 md:hidden">
                <span>
                  Opens:{" "}
                  {new Date(
                    opportunityData.start_intake_date || new Date()
                  ).toLocaleDateString()}
                </span>
                <span>
                  Closes:{" "}
                  {new Date(
                    opportunityData.end_intake_date || new Date()
                  ).toLocaleDateString()}
                </span>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4 text-xs text-gray-600">
              <span>
                Opens:{" "}
                {new Date(
                  opportunityData.start_intake_date || new Date()
                ).toLocaleDateString()}
              </span>
              <span>
                Closes:{" "}
                {new Date(
                  opportunityData.end_intake_date || new Date()
                ).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="pb-32">{renderStep()}</div>
    </div>
  );
}
